import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import type { ButtonProps } from "@chakra-ui/react";
import {
  Copy,
  Download,
  Facebook,
  Instagram,
  MessageCircle,
  QrCode,
  Share2,
  Smartphone,
} from "lucide-react";

import { useAuth } from "../../auth/AuthContext";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

interface BusinessQrInfo {
  businessUid: string;
  businessName?: string;
  generated: boolean;
  generatedNow?: boolean;
  businessCode: string;
  businessUrl: string;
  whatsappMessage: string;
  qrImageUrl: string;
  qrDownloadUrl: string;
}

interface BusinessQrShareActionsProps {
  businessUid: string;
  businessName: string;
}

export interface PickDenguShareButtonProps {
  shareTitle: string;
  shareText: string;
  shareUrl: string;
  modalTitle?: string;
  modalDescription?: string;
  buttonLabel?: string;
  buttonProps?: ButtonProps;
}

type ShareNavigator = Navigator & {
  share?: (data: ShareData) => Promise<void>;
};

const cardsBorderRadii = "8px";

const API_BASE = (
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000"
).replace(/\/+$/, "");

const getFallbackBusinessUrl = (businessUid: string) => {
  const path = `/search-details/businesses/${encodeURIComponent(businessUid)}`;

  if (typeof window === "undefined") {
    return `https://pickdengu.mw${path}`;
  }

  return `${window.location.origin}${path}`;
};

const getErrorMessage = (payload: any, fallback: string) =>
  String(payload?.message || payload?.error || fallback);

export const PickDenguShareButton: React.FC<PickDenguShareButtonProps> = ({
  shareTitle,
  shareText,
  shareUrl,
  modalTitle,
  modalDescription = "Share this PickDengu page.",
  buttonLabel = "Share",
  buttonProps,
}) => {
  const { theme } = useCustomTheme();
  const toast = useToast();
  const shareModal = useDisclosure();

  const resolvedShareUrl = useMemo(() => {
    const cleanUrl = String(shareUrl || "").trim();

    if (!cleanUrl) {
      return "";
    }

    try {
      const baseUrl =
        typeof window !== "undefined"
          ? window.location.origin
          : "https://pickdengu.mw";

      return new URL(cleanUrl, baseUrl).toString();
    } catch {
      return cleanUrl;
    }
  }, [shareUrl]);

  const copyShareLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(resolvedShareUrl);

      toast({
        title: "Link copied",
        description: "The PickDengu link is ready to share.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to copy share link:", error);

      toast({
        title: "Could not copy the link",
        description: resolvedShareUrl,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  }, [resolvedShareUrl, toast]);

  const shareWithNativeApps = useCallback(async () => {
    const shareNavigator = navigator as ShareNavigator;

    if (!shareNavigator.share) {
      return false;
    }

    try {
      await shareNavigator.share({
        title: shareTitle,
        text: shareText,
        url: resolvedShareUrl,
      });

      return true;
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        console.error("Native share failed:", error);
      }

      return false;
    }
  }, [resolvedShareUrl, shareText, shareTitle]);

  const openFacebookShare = () => {
    if (!resolvedShareUrl) {
      toast({
        title: "Share link unavailable",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      resolvedShareUrl,
    )}`;

    window.open(url, "_blank", "noopener,noreferrer,width=720,height=620");
  };

  const openWhatsAppShare = () => {
    if (!resolvedShareUrl) {
      toast({
        title: "Share link unavailable",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const message = encodeURIComponent(`${shareText}\n${resolvedShareUrl}`);

    window.open(
      `https://wa.me/?text=${message}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const openInstagramShare = async () => {
    const shared = await shareWithNativeApps();

    if (shared) {
      return;
    }

    await copyShareLink();

    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");

    toast({
      title: "Link copied for Instagram",
      description:
        "Paste the copied PickDengu link into your Instagram story, message, or bio.",
      status: "info",
      duration: 4200,
      isClosable: true,
    });
  };

  const openMoreApps = async () => {
    const shared = await shareWithNativeApps();

    if (!shared) {
      await copyShareLink();
    }
  };

  return (
    <>
      <Button
        {...buttonProps}
        leftIcon={<Icon as={Share2} boxSize={4} />}
        onClick={shareModal.onOpen}
        whiteSpace="nowrap"
      >
        {buttonLabel}
      </Button>

      <Modal
        isOpen={shareModal.isOpen}
        onClose={shareModal.onClose}
        isCentered
        size={{
          base: "sm",
          md: "md",
        }}
      >
        <ModalOverlay bg="blackAlpha.700" />

        <ModalContent
          bg={theme.surface}
          color={theme.text}
          borderRadius={cardsBorderRadii}
          mx={4}
          border="1px solid"
          borderColor={theme.border}
        >
          <ModalHeader pr={12}>{modalTitle || shareTitle}</ModalHeader>

          <ModalCloseButton />

          <ModalBody pb={5}>
            <Text color={theme.textMuted} fontSize="sm" mb={4}>
              {modalDescription}
            </Text>

            <SimpleGrid
              columns={{
                base: 2,
                md: 3,
              }}
              spacing={3}
            >
              <ShareOption
                label="Facebook"
                icon={Facebook}
                iconColor="#1877F2"
                iconBackground="rgba(24, 119, 242, 0.12)"
                hoverBorderColor="#1877F2"
                onClick={openFacebookShare}
              />

              <ShareOption
                label="WhatsApp"
                icon={MessageCircle}
                iconColor="#16A34A"
                iconBackground="rgba(37, 211, 102, 0.14)"
                hoverBorderColor="#25D366"
                onClick={openWhatsAppShare}
              />

              <ShareOption
                label="Instagram"
                icon={Instagram}
                iconColor="white"
                iconGradient="linear(to-br, #F58529, #DD2A7B, #8134AF, #515BD4)"
                hoverBorderColor="#DD2A7B"
                onClick={openInstagramShare}
              />

              <ShareOption
                label="More apps"
                icon={Smartphone}
                iconColor="#7C3AED"
                iconBackground="rgba(124, 58, 237, 0.13)"
                hoverBorderColor="#7C3AED"
                onClick={openMoreApps}
              />

              <ShareOption
                label="Copy link"
                icon={Copy}
                iconColor="#0284C7"
                iconBackground="rgba(14, 165, 233, 0.13)"
                hoverBorderColor="#0EA5E9"
                onClick={copyShareLink}
              />
            </SimpleGrid>

            <Divider my={5} borderColor={theme.border} />

            <Text fontSize="xs" color={theme.textMuted} wordBreak="break-all">
              {resolvedShareUrl}
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

const BusinessQrShareActions: React.FC<BusinessQrShareActionsProps> = ({
  businessUid,
  businessName,
}) => {
  const { user } = useAuth();
  const { theme } = useCustomTheme();
  const toast = useToast();

  const qrModal = useDisclosure();

  const [loadingInfo, setLoadingInfo] = useState(true);

  const [generating, setGenerating] = useState(false);

  const [downloading, setDownloading] = useState(false);

  const [qrInfo, setQrInfo] = useState<BusinessQrInfo | null>(null);

  const isOwner = Boolean(user?.uid && user.uid === businessUid);

  const businessUrl =
    qrInfo?.businessUrl || getFallbackBusinessUrl(businessUid);

  const shareTitle = `${businessName} on PickDengu`;

  const qrInfoUrl = useMemo(
    () =>
      `${API_BASE}/api/v1/whatsapp/qr/business/${encodeURIComponent(
        businessUid,
      )}/info`,
    [businessUid],
  );

  const loadQrInfo = useCallback(async () => {
    if (!businessUid) {
      setLoadingInfo(false);
      return;
    }

    try {
      setLoadingInfo(true);

      const response = await fetch(qrInfoUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          getErrorMessage(payload, "Failed to load QR information."),
        );
      }

      setQrInfo(payload?.data || null);
    } catch (error) {
      console.error("Failed to load business QR information:", error);

      setQrInfo({
        businessUid,
        businessName,
        generated: false,
        businessCode: "",
        businessUrl: getFallbackBusinessUrl(businessUid),
        whatsappMessage: "",
        qrImageUrl: "",
        qrDownloadUrl: "",
      });
    } finally {
      setLoadingInfo(false);
    }
  }, [businessName, businessUid, qrInfoUrl]);

  useEffect(() => {
    void loadQrInfo();
  }, [loadQrInfo]);

  const copyBusinessLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(businessUrl);

      toast({
        title: "Business link copied",
        description: "The public PickDengu business link is ready to share.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to copy business link:", error);

      toast({
        title: "Could not copy the link",
        description: businessUrl,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  }, [businessUrl, toast]);

  const copyBusinessCode = useCallback(async () => {
    if (!qrInfo?.businessCode) {
      return;
    }

    try {
      await navigator.clipboard.writeText(qrInfo.businessCode);

      toast({
        title: "Business code copied",
        status: "success",
        duration: 2200,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to copy business code:", error);
    }
  }, [qrInfo?.businessCode, toast]);

  const generateQrCode = async () => {
    if (!user || !isOwner) {
      toast({
        title: "Owner access required",
        description:
          "Only the logged-in owner can generate this business QR code.",
        status: "warning",
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    try {
      setGenerating(true);

      const idToken = await user.getIdToken();

      const response = await fetch(
        `${API_BASE}/api/v1/public/businesses/${encodeURIComponent(
          businessUid,
        )}/qr/generate`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        },
      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          getErrorMessage(payload, "Failed to generate the QR code."),
        );
      }

      const generatedInfo = payload?.data as BusinessQrInfo;

      setQrInfo(generatedInfo);

      qrModal.onOpen();

      toast({
        title: generatedInfo?.generatedNow
          ? "QR code generated"
          : "QR code ready",
        description: generatedInfo?.businessCode
          ? `WhatsApp business code: ${generatedInfo.businessCode}`
          : "The business QR code is ready.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Business QR generation failed:", error);

      toast({
        title: "QR generation failed",
        description: error?.message || "Please try again.",
        status: "error",
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleQrAction = () => {
    if (qrInfo?.generated) {
      qrModal.onOpen();
      return;
    }

    void generateQrCode();
  };

  const downloadQrCode = async () => {
    if (!qrInfo?.generated || !qrInfo.qrDownloadUrl) {
      return;
    }

    try {
      setDownloading(true);

      const response = await fetch(qrInfo.qrDownloadUrl);

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));

        throw new Error(
          getErrorMessage(payload, "Failed to download the QR code."),
        );
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = objectUrl;
      link.download = `pickdengu-${qrInfo.businessCode || businessUid}-qr.png`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(objectUrl);
    } catch (error: any) {
      console.error("Business QR download failed:", error);

      toast({
        title: "Download failed",
        description: error?.message || "Please try again.",
        status: "error",
        duration: 3800,
        isClosable: true,
      });
    } finally {
      setDownloading(false);
    }
  };

  const showQrAction = Boolean(qrInfo?.generated) || isOwner;

  return (
    <>
      <HStack
        spacing={2}
        flexWrap="wrap"
        justify={{
          base: "flex-start",
          md: "flex-end",
        }}
      >
        <PickDenguShareButton
          shareTitle={shareTitle}
          shareText={`Browse ${businessName}'s products and services on PickDengu.`}
          shareUrl={businessUrl}
          modalTitle={`Share ${businessName}`}
          modalDescription="Share this public PickDengu business page."
          buttonProps={{
            size: "sm",
            borderRadius: {cardsBorderRadii},
            variant: "outline",
            borderColor: theme.borderStrong || theme.border,
            color: theme.text,
            bg: theme.surface,
            _hover: {
              bg: theme.surfaceAlt,
            },
          }}
        />

        {loadingInfo && isOwner ? (
          <Button
            size="sm"
            borderRadius={cardsBorderRadii}
            variant="outline"
            isDisabled
            leftIcon={<Spinner size="xs" />}
            whiteSpace="nowrap"
          >
            QR Code
          </Button>
        ) : null}

        {!loadingInfo && showQrAction ? (
          <Button
            size="sm"
            leftIcon={<Icon as={QrCode} boxSize={4} />}
            onClick={handleQrAction}
            isLoading={generating}
            loadingText="Generating"
            borderRadius={cardsBorderRadii}
            color={theme.textOnPrimary || "white"}
            bgImage={theme.gradients?.primaryButton || theme.primary}
            _hover={{
              bgImage:
                theme.gradients?.primaryButtonHover || theme.primaryHover,
            }}
            whiteSpace="nowrap"
          >
            {qrInfo?.generated ? "Show QR Code" : "Generate QR Code"}
          </Button>
        ) : null}
      </HStack>

      <Modal
        isOpen={qrModal.isOpen}
        onClose={qrModal.onClose}
        isCentered
        size={{
          base: "sm",
          md: "md",
        }}
      >
        <ModalOverlay bg="blackAlpha.700" />

        <ModalContent
          bg={theme.surface}
          color={theme.text}
          borderRadius={cardsBorderRadii}
          mx={4}
          border="1px solid"
          borderColor={theme.border}
        >
          <ModalHeader pr={12}>{businessName} QR Code</ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4} align="stretch">
              {qrInfo?.generated && qrInfo.qrImageUrl ? (
                <Box
                  p={4}
                  bg="white"
                  borderRadius={cardsBorderRadii}
                  border="1px solid"
                  borderColor={theme.border}
                  mx="auto"
                >
                  <Image
                    src={`${qrInfo.qrImageUrl}?v=${encodeURIComponent(
                      qrInfo.businessCode,
                    )}`}
                    alt={`${businessName} PickDengu QR code`}
                    boxSize={{
                      base: "230px",
                      md: "280px",
                    }}
                    objectFit="contain"
                  />
                </Box>
              ) : (
                <VStack
                  minH="260px"
                  justify="center"
                  spacing={3}
                  bg={theme.surfaceAlt}
                  borderRadius={cardsBorderRadii}
                >
                  <Spinner />

                  <Text color={theme.textMuted} fontSize="sm">
                    Preparing QR code...
                  </Text>
                </VStack>
              )}

              {qrInfo?.businessCode ? (
                <Box
                  p={4}
                  bg={theme.surfaceAlt}
                  borderRadius={cardsBorderRadii}
                  border="1px solid"
                  borderColor={theme.border}
                >
                  <Text
                    fontSize="xs"
                    color={theme.textMuted}
                    fontWeight="800"
                    textTransform="uppercase"
                    letterSpacing="0.08em"
                  >
                    WhatsApp business code
                  </Text>

                  <Flex mt={1} align="center" justify="space-between" gap={3}>
                    <Text
                      fontSize={{
                        base: "lg",
                        md: "xl",
                      }}
                      fontWeight="900"
                      letterSpacing="0.04em"
                      color={theme.primary}
                      wordBreak="break-word"
                    >
                      {qrInfo.businessCode}
                    </Text>

                    <Button
                      size="xs"
                      variant="ghost"
                      leftIcon={<Icon as={Copy} boxSize={3.5} />}
                      onClick={copyBusinessCode}
                      whiteSpace="nowrap"
                    >
                      Copy
                    </Button>
                  </Flex>

                  <Text mt={2} fontSize="sm" color={theme.textMuted}>
                    Customers can send <strong>Hi {qrInfo.businessCode}</strong>{" "}
                    to PickDengu on WhatsApp.
                  </Text>
                </Box>
              ) : null}

              <Text fontSize="sm" color={theme.textMuted} textAlign="center">
                The QR code opens this public PickDengu business page.
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter gap={3} flexWrap="wrap">
            <Button
              leftIcon={<Icon as={Copy} boxSize={4} />}
              variant="outline"
              borderRadius={cardsBorderRadii}
              onClick={copyBusinessLink}
              whiteSpace="nowrap"
            >
              Copy Link
            </Button>

            <Button
              leftIcon={<Icon as={Download} boxSize={4} />}
              onClick={() => void downloadQrCode()}
              isLoading={downloading}
              loadingText="Downloading"
              borderRadius={cardsBorderRadii}
              color={theme.textOnPrimary || "white"}
              bgImage={theme.gradients?.primaryButton || theme.primary}
              _hover={{
                bgImage:
                  theme.gradients?.primaryButtonHover || theme.primaryHover,
              }}
              whiteSpace="nowrap"
            >
              Download QR Code
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const ShareOption = ({
  label,
  icon,
  iconColor,
  iconBackground,
  iconGradient,
  hoverBorderColor,
  onClick,
}: {
  label: string;
  icon: React.ElementType;
  iconColor: string;
  iconBackground?: string;
  iconGradient?: string;
  hoverBorderColor: string;
  onClick: () => void | Promise<unknown>;
}) => {
  const { theme } = useCustomTheme();

  return (
    <Button
      role="group"
      h="92px"
      borderRadius={cardsBorderRadii}
      variant="outline"
      borderColor={theme.border}
      color={theme.text}
      bg={theme.surfaceAlt}
      whiteSpace="normal"
      onClick={() => {
        void onClick();
      }}
      _hover={{
        bg: theme.surface,
        borderColor: hoverBorderColor,
        transform: "translateY(-2px)",
        boxShadow: `0 8px 22px ${iconBackground || "rgba(15, 23, 42, 0.10)"}`,
      }}
      transition="all 0.2s ease"
    >
      <VStack spacing={2}>
        <Flex
          boxSize="36px"
          align="center"
          justify="center"
          borderRadius={cardsBorderRadii}
          bg={iconGradient ? undefined : iconBackground}
          bgGradient={iconGradient}
          boxShadow={
            iconGradient ? "0 6px 16px rgba(221, 42, 123, 0.24)" : "none"
          }
          transition="all 0.2s ease"
          _groupHover={{
            transform: "scale(1.06)",
          }}
        >
          <Icon as={icon} boxSize={5} color={iconColor} strokeWidth={2.2} />
        </Flex>

        <Text fontSize="xs" fontWeight="800">
          {label}
        </Text>
      </VStack>
    </Button>
  );
};

export default BusinessQrShareActions;
